import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface AdminKPIMetrics {
  total_companies: number;
  total_users: number;
  daily_active_users: number;
  notification_open_rate: number;
  notification_avg_time_to_open: number;
  notification_action_completion_rate: number;
  notification_unread_count: number;
}

export function useAdminKPIMetrics() {
  return useQuery<AdminKPIMetrics>({
    queryKey: ['admin', 'kpi-metrics'],
    staleTime: 60_000, // 1 minute
    queryFn: async () => {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Get total companies
      const { count: totalCompanies } = await supabase
        .from('companies')
        .select('id', { count: 'exact', head: true });

      // Get total users
      const { count: totalUsers } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true });

      // Get daily active users (users who logged in today)
      const { count: dailyActiveUsers } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .gte('last_login_at', todayStart.toISOString());

      // Get notification analytics — gracefully handle missing RPC function
      let notificationData = {
        open_rate: 0,
        avg_time_to_open_seconds: 0,
        action_completion_rate: 0,
        unread_count: 0,
      };
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: notificationAnalytics, error: rpcError } = await (supabase.rpc as any)('get_notification_analytics', {
          p_user_id: null, // null = all users for admin
          p_start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Last 30 days
          p_end_date: now.toISOString(),
        });

        if (!rpcError && notificationAnalytics && Array.isArray(notificationAnalytics) && notificationAnalytics.length > 0) {
          notificationData = notificationAnalytics[0];
        }
      } catch {
        // RPC function may not exist in this environment — use defaults
      }

      return {
        total_companies: totalCompanies || 0,
        total_users: totalUsers || 0,
        daily_active_users: dailyActiveUsers || 0,
        notification_open_rate: notificationData.open_rate || 0,
        notification_avg_time_to_open: notificationData.avg_time_to_open_seconds || 0,
        notification_action_completion_rate: notificationData.action_completion_rate || 0,
        notification_unread_count: notificationData.unread_count || 0,
      };
    },
  });
}

